import React, { useState, useEffect } from 'react';
import { Calendar, Download, Filter, Search, DollarSign, FileText, TrendingUp, Users } from 'lucide-react';
import { Button, Input, Select } from '../ui';
import SearchableSelect from '../ui/SearchableSelect';
import MultiSearchableSelect from '../ui/MultiSearchableSelect';
import FileViewerModal from '../common/FileViewerModal';
import { PAYMENT_CHANNELS } from '../../utils/bankAccounts';
import api from '../../services/api';

interface PaymentRecord {
  id: number;
  itinerary_id: number;
  guest_name: string;
  vendor_name: string;
  payment_amount: number;
  payment_date: string;
  payment_reference: string;
  payment_method: string;
  bank_account: string;
  status: string;
  created_at: string;
  file_count: number;
  has_files: boolean;
  quotation_number?: string;
  version_number?: number;
}

interface PaymentSummary {
  total_payments: number;
  total_amount: number;
  payment_count: number;
  avg_payment: number;
  top_vendor: string;
  top_vendor_amount: number;
}

const PaymentsReportPage: React.FC = () => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentRecord[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [sortField, setSortField] = useState<string>('payment_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Filters
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    dateTo: new Date().toISOString().split('T')[0], // today
    travelDateFrom: '',
    travelDateTo: '',
    vendor: '',
    selectedVendors: [] as string[],
    vendorFilterMode: 'include' as 'include' | 'exclude',
    paymentMethod: '',
    minAmount: '',
    maxAmount: '',
    searchQuery: '',
    itineraryId: '',
    guestName: ''
  });
  
  const [showFilters, setShowFilters] = useState(false); // Collapsed by default on mobile
  const [isMobile, setIsMobile] = useState(false);
  const [vendors, setVendors] = useState<string[]>([]);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [paymentFiles, setPaymentFiles] = useState<any[]>([]);
  const [viewerState, setViewerState] = useState<{ isOpen: boolean; fileUrl: string; fileName: string; fileType: 'pdf' | 'image' }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '', 
    fileType: 'image' 
  });

  useEffect(() => {
    loadPaymentsData();
    loadVendors();
    
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [payments, filters]);

  useEffect(() => {
    loadPaymentsData();
  }, [sortField, sortDirection]);

  const handleViewFile = (fileUrl: string, fileName: string) => {
    console.log('S3 URL for file viewing:', fileUrl);
    
    const fileExt = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt || '')) {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'image' });
    } else if (fileExt === 'pdf') {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'pdf' });
    } else {
      // For other file types, try direct download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    }
  };

  const loadPaymentFiles = async (paymentId: number) => {
    try {
      const response = await api.get(`/reports/payments/${paymentId}/files`);
      if (response.data.success) {
        setPaymentFiles(response.data.data || []);
        setSelectedPaymentId(paymentId);
        setShowFilesModal(true);
      }
    } catch (error) {
      console.error('Error loading payment files:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const loadPaymentsData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/vendor-payments', {
        params: {
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          travel_date_from: filters.travelDateFrom,
          travel_date_to: filters.travelDateTo,
          vendor: filters.vendor,
          payment_method: filters.paymentMethod,
          min_amount: filters.minAmount,
          max_amount: filters.maxAmount,
          search: filters.searchQuery,
          page: currentPage,
          limit: itemsPerPage,
          sort_field: sortField,
          sort_direction: sortDirection,
          itinerary_id: filters.itineraryId,
          guest_name: filters.guestName
        }
      });
      
      if (response.data.success) {
        setPayments(response.data.data.payments || []);
        setFilteredPayments(response.data.data.payments || []); // No client filtering needed
        setSummary(response.data.data.summary || null);
      }
    } catch (error) {
      console.error('Error loading payments data:', error);
      setPayments([]);
      setFilteredPayments([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get('/accounts-v3/vendors');
      if (response.data.success) {
        setVendors(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...payments];

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(p => p.payment_date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      filtered = filtered.filter(p => p.payment_date <= filters.dateTo);
    }

    // Vendor filter
    if (filters.vendor) {
      filtered = filtered.filter(p => 
        p.vendor_name?.toLowerCase().includes(filters.vendor.toLowerCase())
      );
    }
    if (filters.selectedVendors.length > 0) {
      filtered = filtered.filter(p =>
        filters.vendorFilterMode === 'include'
          ? filters.selectedVendors.includes(p.vendor_name)
          : !filters.selectedVendors.includes(p.vendor_name)
      );
    }

    // Payment method filter - check bank_account field
    if (filters.paymentMethod) {
      filtered = filtered.filter(p => p.bank_account === filters.paymentMethod);
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(p => p.payment_amount >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(p => p.payment_amount <= parseFloat(filters.maxAmount));
    }

    // Search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.guest_name?.toLowerCase().includes(query) ||
        p.vendor_name?.toLowerCase().includes(query) ||
        p.itinerary_id?.toString().includes(query) ||
        p.payment_reference?.toLowerCase().includes(query)
      );
    }

    setFilteredPayments(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    loadPaymentsData();
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    try {
      setExporting(true);
      
      if (format === 'excel') {
        // Create Excel file
        const XLSX = await import('xlsx');
        const worksheet = XLSX.utils.json_to_sheet(
          filteredPayments.map(payment => ({
            'Date': formatDate(payment.payment_date),
            'Itinerary ID': `WH_${payment.itinerary_id}`,
            'Guest Name': payment.guest_name,
            'Vendor': payment.vendor_name,
            'Amount (IDR)': payment.payment_amount,
            'Payment Channel': payment.bank_account || '-',
            'Status': payment.status
          }))
        );
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments Report');
        XLSX.writeFile(workbook, `payments-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      } else {
        // Create PDF file
        const jsPDF = (await import('jspdf')).default;
        const autoTable = (await import('jspdf-autotable')).default;
        
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(16);
        doc.text('Payments Report', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);
        
        // Add summary
        doc.text(`Total Payments: ${filteredSummary.payment_count}`, 14, 35);
        doc.text(`Total Amount: ${formatCurrency(filteredSummary.total_amount)}`, 14, 42);
        
        // Add table
        autoTable(doc, {
          startY: 50,
          head: [['Date', 'Itinerary', 'Guest', 'Vendor', 'Amount (IDR)', 'Payment Channel']],
          body: filteredPayments.map(payment => [
            formatDate(payment.payment_date),
            `WH_${payment.itinerary_id}`,
            payment.guest_name,
            payment.vendor_name,
            payment.payment_amount.toLocaleString('id-ID'),
            payment.bank_account || '-'
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246] }
        });
        
        doc.save(`payments-report-${new Date().toISOString().split('T')[0]}.pdf`);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const calculateFilteredSummary = () => {
    const totalAmount = filteredPayments.reduce((sum, payment) => sum + payment.payment_amount, 0);
    const paymentCount = filteredPayments.length;
    const avgPayment = paymentCount > 0 ? totalAmount / paymentCount : 0;
    
    // Find top vendor from filtered data
    const vendorTotals = filteredPayments.reduce((acc, payment) => {
      acc[payment.vendor_name] = (acc[payment.vendor_name] || 0) + payment.payment_amount;
      return acc;
    }, {} as Record<string, number>);
    
    const topVendorEntry = Object.entries(vendorTotals).sort(([,a], [,b]) => b - a)[0];
    const topVendor = topVendorEntry ? topVendorEntry[0] : 'N/A';
    const topVendorAmount = topVendorEntry ? topVendorEntry[1] : 0;
    
    return {
      total_payments: paymentCount,
      total_amount: totalAmount,
      payment_count: paymentCount,
      avg_payment: avgPayment,
      top_vendor: topVendor,
      top_vendor_amount: topVendorAmount
    };
  };

  const filteredSummary = calculateFilteredSummary();
  
  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading payments report...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-8">
          {/* Header */}
          <div className="bg-white rounded-lg sm:rounded-2xl p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Vendor Payments Report</h1>
                <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Track payments made to vendors</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col gap-4">
              {/* Filter Toggle */}
              <Button
                variant="secondary"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </Button>
            </div>
            
            {/* Date and Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date From</label>
                    <Input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => {
                        handleFilterChange('dateFrom', e.target.value);
                        if (e.target.value && !filters.dateTo) {
                          handleFilterChange('dateTo', e.target.value);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date To</label>
                    <Input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
                    <Input
                      type="date"
                      value={filters.travelDateFrom || ''}
                      onChange={(e) => handleFilterChange('travelDateFrom', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
                    <Input
                      type="date"
                      value={filters.travelDateTo || ''}
                      onChange={(e) => handleFilterChange('travelDateTo', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <Input
                      type="text"
                      value={filters.searchQuery}
                      onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                      placeholder="Guest, vendor..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
                    <Input
                      type="text"
                      value={filters.itineraryId}
                      onChange={(e) => handleFilterChange('itineraryId', e.target.value)}
                      placeholder="e.g. 5749"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name</label>
                    <Input
                      type="text"
                      value={filters.guestName}
                      onChange={(e) => handleFilterChange('guestName', e.target.value)}
                      placeholder="Guest name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        type="button"
                        onClick={() => setFilters(prev => ({...prev, vendorFilterMode: prev.vendorFilterMode === 'include' ? 'exclude' : 'include'}))}
                        className={`px-2 py-0.5 text-xs font-medium rounded-full border transition-colors ${filters.vendorFilterMode === 'include' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}
                      >
                        {filters.vendorFilterMode === 'include' ? '✓ Include' : '✕ Exclude'}
                      </button>
                    </div>
                    <MultiSearchableSelect
                      options={vendors.filter(v => v !== 'All Vendors').map(v => ({ value: v, label: v }))}
                      value={filters.selectedVendors}
                      onChange={(value) => setFilters(prev => ({...prev, selectedVendors: value as string[]}))}
                      placeholder="All Vendors"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <Select
                      value={filters.paymentMethod}
                      onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                    >
                      <option value="">All Methods</option>
                      {PAYMENT_CHANNELS.map(channel => (
                        <option key={channel} value={channel}>{channel}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount (IDR)</label>
                    <Input
                      type="number"
                      value={filters.minAmount}
                      onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount (IDR)</label>
                    <Input
                      type="number"
                      value={filters.maxAmount}
                      onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                      placeholder="No limit"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                  <Button onClick={handleSearch} className="flex items-center justify-center gap-2">
                    <Search className="w-4 h-4" />
                    Apply Filters
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setFilters({
                        dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        dateTo: new Date().toISOString().split('T')[0],
                        travelDateFrom: '',
                        travelDateTo: '',
                        vendor: '',
                        selectedVendors: [] as string[],
                        vendorFilterMode: 'include' as 'include' | 'exclude',
                        paymentMethod: '',
                        minAmount: '',
                        maxAmount: '',
                        searchQuery: '',
                        itineraryId: '',
                        guestName: ''
                      });
                    }}
                    className="flex items-center justify-center gap-2"
                  >
                    Clear Filters
                  </Button>
                </div>
                
                {/* Clear Filters Link */}
                {(filters.searchQuery || filters.dateFrom || filters.dateTo || filters.vendor || filters.selectedVendors.length > 0 || filters.paymentMethod || filters.minAmount || filters.maxAmount) && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        setFilters({
                          dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                          dateTo: new Date().toISOString().split('T')[0],
                          travelDateFrom: '',
                          travelDateTo: '',
                          vendor: '',
                          selectedVendors: [] as string[],
                          vendorFilterMode: 'include' as 'include' | 'exclude',
                          paymentMethod: '',
                          minAmount: '',
                          maxAmount: '',
                          searchQuery: '',
                          itineraryId: '',
                          guestName: ''
                        });
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Payments</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{filteredSummary.payment_count}</p>
                </div>
                <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
                  <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Amount</p>
                  <p className="text-sm sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(filteredSummary.total_amount)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-green-100 rounded-full flex-shrink-0">
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Average Payment</p>
                  <p className="text-sm sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(filteredSummary.avg_payment)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-purple-100 rounded-full flex-shrink-0">
                  <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Top Vendor</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">{filteredSummary.top_vendor}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{formatCurrency(filteredSummary.top_vendor_amount)}</p>
                </div>
                <div className="p-2 sm:p-3 bg-orange-100 rounded-full flex-shrink-0">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Payments Table */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm overflow-hidden">
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Payment Records ({filteredPayments.length})
              </h3>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Button
                  onClick={() => handleExport('excel')}
                  disabled={exporting}
                  className="flex items-center gap-1 sm:gap-2 bg-green-600 hover:bg-green-700 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  Excel
                </Button>
                <Button
                  onClick={() => handleExport('pdf')}
                  disabled={exporting}
                  className="flex items-center gap-1 sm:gap-2 bg-red-600 hover:bg-red-700 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  PDF
                </Button>
              </div>
            </div>
            
            {/* Mobile Card View */}
            {isMobile ? (
              <div className="space-y-3 p-3">
                {paginatedPayments.map((payment) => (
                  <div key={payment.id} className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {payment.quotation_number ? (
                            <><span className="font-mono font-bold text-blue-700">{payment.quotation_number}/{payment.version_number}</span><span className="text-[10px] text-gray-400 ml-1">WH_{payment.itinerary_id}</span></>
                          ) : (
                            <span className="text-blue-600">WH_{payment.itinerary_id}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(payment.payment_date)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900 text-sm">{formatCurrency(payment.payment_amount)}</div>
                        {payment.has_files && (
                          <button
                            onClick={() => loadPaymentFiles(payment.id)}
                            className="text-green-600 hover:text-green-800 flex items-center justify-end mt-1"
                            title={`${payment.file_count} file(s)`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            <span className="text-xs">{payment.file_count}</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-gray-900 truncate" title={payment.guest_name}>
                        <span className="text-gray-500">Guest:</span> {payment.guest_name}
                      </div>
                      <div className="text-sm text-gray-900 truncate" title={payment.vendor_name}>
                        <span className="text-gray-500">Vendor:</span> {payment.vendor_name}
                      </div>
                      {payment.bank_account && (
                        <div className="text-sm text-gray-900 truncate" title={payment.bank_account}>
                          <span className="text-gray-500">Channel:</span> {payment.bank_account}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('payment_date')}
                    >
                      <div className="flex items-center">
                        Date
                        {sortField === 'payment_date' && (
                          <span className="ml-1 text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Itinerary
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('guest_name')}
                    >
                      <div className="flex items-center">
                        Guest
                        {sortField === 'guest_name' && (
                          <span className="ml-1 text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('vendor_name')}
                    >
                      <div className="flex items-center">
                        Vendor
                        {sortField === 'vendor_name' && (
                          <span className="ml-1 text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('payment_amount')}
                    >
                      <div className="flex items-center justify-end">
                        Amount
                        {sortField === 'payment_amount' && (
                          <span className="ml-1 text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Channel
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Files
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                        {payment.quotation_number ? (
                          <div>
                            <div className="font-mono font-bold text-blue-700">{payment.quotation_number}/{payment.version_number}</div>
                            <div className="text-[10px] text-gray-400">WH_{payment.itinerary_id}</div>
                          </div>
                        ) : (
                          <span className="text-blue-600">WH_{payment.itinerary_id}</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900">
                        <div className="max-w-[120px] sm:max-w-none truncate" title={payment.guest_name}>
                          {payment.guest_name}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900">
                        <div className="max-w-[120px] sm:max-w-none truncate" title={payment.vendor_name}>
                          {payment.vendor_name}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 text-right">
                        <div className="truncate">
                          {formatCurrency(payment.payment_amount)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900">
                        <div className="max-w-[120px] sm:max-w-none truncate" title={payment.bank_account || '-'}>
                          {payment.bank_account || '-'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-center">
                        {payment.has_files ? (
                          <button
                            onClick={() => loadPaymentFiles(payment.id)}
                            className="text-green-600 hover:text-green-800 flex items-center justify-center mx-auto"
                            title={`${payment.file_count} file(s)`}
                          >
                            <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="text-xs">{payment.file_count}</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            )}

            {filteredPayments.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
                <p className="text-gray-500">No payments match the selected filters.</p>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredPayments.length)} of {filteredPayments.length} results
                </div>
                <div className="flex items-center justify-center sm:justify-end space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm"
                  >
                    Previous
                  </Button>
                  <span className="text-xs sm:text-sm text-gray-700 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Files Modal */}
      {showFilesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Payment Files - ID: {selectedPaymentId}
              </h3>
              <button
                onClick={() => setShowFilesModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>
            
            {paymentFiles.length > 0 ? (
              <div className="space-y-3">
                {paymentFiles.map((file, index) => (
                  <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-gray-200 rounded-lg gap-3">
                    <div className="flex items-center min-w-0 flex-1">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{file.filename}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{file.file_type}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewFile(file.url, file.filename)}
                      className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-600 rounded hover:bg-blue-50 cursor-pointer text-sm flex-shrink-0"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2 sm:mb-4" />
                <p className="text-sm sm:text-base">No files found for this payment.</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      <FileViewerModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        fileUrl={viewerState.fileUrl}
        fileName={viewerState.fileName}
        fileType={viewerState.fileType}
      />
    </div>
  );
};

export default PaymentsReportPage;